import { z } from "zod/v4";

/**
 * The announcements API: support-only feature-announcement emails, served by
 * the root client API (no tenant) under {@link ANNOUNCEMENTS_BASE_PATH}.
 * Declared once here for the Lambda and the web page, in the root API style
 * of `apps/a3s-dir/src/client-api/schema.ts`.
 */

export const ANNOUNCEMENTS_BASE_PATH = "/api/v1/announcements";

/** Only SendGrid dynamic templates named with this prefix can be announced. */
export const ANNOUNCEMENT_TEMPLATE_NAME_PREFIX = "[Announcement]";

/** A SendGrid dynamic template id. */
export const AnnouncementTemplateId = z.string().regex(/^d-[0-9a-f]+$/, "Must be a SendGrid dynamic template id");

export const AnnouncementSubject = z.string().trim().min(1, "Subject is required").max(255);

// #region Errors

/**
 * Why a request was refused. Every refusal carries one, so the page decides
 * what to show from it, never from the message text.
 */
export const AnnouncementErrorReason = z.enum([
  "forbidden",
  "invalidRequest",
  "uploadTooLarge",
  /** No unsubscribe group is configured for this environment. */
  "notConfigured",
  /** `SENDGRID_API_KEY` is not in the root secret yet. */
  "sendGridNotConfigured",
  "templateRejected",
  /** There is no such list, or it has expired. */
  "listNotFound",
  /** The list has expired: upload it again. */
  "listExpired",
  /** The list expires within the hour, too soon to send to: upload it again. */
  "listExpiring",
  "invalidRows",
  "tooManyRecipients",
  "emptyList",
  /** The list is beyond what can be stored. The caps make this unreachable for a valid list. */
  "listTooLarge",
  /** A resend named an earlier send that does not exist. */
  "replacedSendNotFound",
  "callerEmailUnavailable",
  /** Ran out of time before anything was stored or sent; repeat the request. */
  "timedOut",
  /** The same template and subject were sent (or attempted) within 24 hours. */
  "alreadySent",
  /** A request with this idempotency key is still queueing. */
  "inProgress",
  /** A request with this idempotency key stopped while queueing; it may have sent some emails. */
  "stalePending",
  /** Queueing stopped part way: some emails went, the rest did not. */
  "partiallyQueued",
  "internal",
]);
export type AnnouncementErrorReason = z.infer<typeof AnnouncementErrorReason>;

export const PreviousAnnouncementSend = z.object({
  sendId: z.string(),
  sentByEmail: z.string(),
  createdAt: z.string(),
  recipientCount: z.number().int().nonnegative(),
  status: z.enum(["pending", "queued", "failed"]),
  /** `pending` long after its Lambda must have stopped. */
  stale: z.boolean(),
});
export type PreviousAnnouncementSend = z.infer<typeof PreviousAnnouncementSend>;

export const AnnouncementErrorBody = z.object({
  message: z.string(),
  reason: AnnouncementErrorReason,
  /** `invalidRows`: row numbers, counting the header as row 1. */
  invalidRows: z.array(z.number().int()).optional(),
  /** `alreadySent`: the send holding the 24-hour guard. */
  previous: PreviousAnnouncementSend.optional(),
  /** `inProgress`, `stalePending`, `partiallyQueued`: the send concerned. */
  sendId: z.string().optional(),
});
export type AnnouncementErrorBody = z.infer<typeof AnnouncementErrorBody>;

// #endregion

// #region Templates

export const AnnouncementTemplate = z.object({
  id: AnnouncementTemplateId,
  name: z.string(),
  /** When the active version was last edited in SendGrid; `null` when the template has no active version. */
  activeVersionUpdatedAt: z.string().nullable(),
  /** Whether the template can be sent: active version present, with an unsubscribe link. */
  hasUnsubscribeTag: z.boolean(),
});
export type AnnouncementTemplate = z.infer<typeof AnnouncementTemplate>;

export const ListAnnouncementTemplatesResponse = z.object({
  templates: z.array(AnnouncementTemplate),
});
export type ListAnnouncementTemplatesResponse = z.infer<typeof ListAnnouncementTemplatesResponse>;

// #endregion

// #region Lists

/**
 * A stored recipient list, without its members (up to 50,000): they are
 * never returned, only a sample of the first few to check the file by.
 * Lists are never edited, and expire 30 days after upload; a changed list is
 * a new upload.
 */
export const RecipientList = z.object({
  id: z.string(),
  createdBy: z.string(),
  createdByEmail: z.string(),
  createdAt: z.string(),
  /** When the list stops being usable; a send is refused within the hour before. */
  expiresAt: z.string(),
  recipientCount: z.number().int().nonnegative(),
  /** Repeated addresses the upload had, counted once each repeat. */
  duplicates: z.number().int().nonnegative(),
  /** Addresses left out because they had unsubscribed when the list was uploaded. */
  unsubscribed: z.number().int().nonnegative(),
  /** The first 20 members, in the order the file gave them. */
  sample: z.array(z.string()),
});
export type RecipientList = z.infer<typeof RecipientList>;

/**
 * Uploading a list: `POST` {@link AnnouncementsPaths.lists} with the file
 * itself as the body (`Content-Type: text/csv`), at most
 * {@link MAX_UPLOAD_BYTES}. Sending it raw, not inside JSON, means the limit
 * is the file's own size. The answer is the stored {@link RecipientList}.
 */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

// #endregion

// #region Sending

export const TestSendAnnouncementBody = z.object({
  templateId: AnnouncementTemplateId,
  subject: AnnouncementSubject,
});
export type TestSendAnnouncementBody = z.infer<typeof TestSendAnnouncementBody>;

export const TestSendAnnouncementResponse = z.object({
  /** The caller's own address, from their Auth0 profile — never from the request. */
  sentTo: z.string(),
});
export type TestSendAnnouncementResponse = z.infer<typeof TestSendAnnouncementResponse>;

export const SendAnnouncementBody = z
  .object({
    listId: z.string().min(1),
    templateId: AnnouncementTemplateId,
    subject: AnnouncementSubject,
    /** Generated once per confirmation dialog, so a retried request never sends twice. */
    idempotencyKey: z.string().min(1).max(128),
    /** Send even though this template and subject were already sent within the last 24 hours. */
    allowResend: z.boolean().optional(),
    /**
     * With `allowResend`, required: the earlier send (`previous.sendId` from
     * the 409) the user chose to send over. Only that send's guard is
     * replaced, so a resend never overrides a send the user was not shown.
     */
    replacesSendId: z.string().min(1).optional(),
  })
  .refine((body) => body.allowResend !== true || body.replacesSendId !== undefined, {
    message: "allowResend needs replacesSendId: the earlier send being sent over",
    path: ["replacesSendId"],
  });
export type SendAnnouncementBody = z.infer<typeof SendAnnouncementBody>;

export const SendAnnouncementResponse = z.object({
  sendId: z.string(),
  /** Recipients whose email was queued. */
  queued: z.number().int().nonnegative(),
  /** List members left out because they unsubscribed after the list was made. */
  removedAsUnsubscribed: z.number().int().nonnegative(),
});
export type SendAnnouncementResponse = z.infer<typeof SendAnnouncementResponse>;

// #endregion

/** Paths, relative to the root API's base URL. */
export const AnnouncementsPaths = {
  templates: `${ANNOUNCEMENTS_BASE_PATH}/templates`,
  /** `POST` a `text/csv` body to upload a list. */
  lists: `${ANNOUNCEMENTS_BASE_PATH}/lists`,
  testSend: `${ANNOUNCEMENTS_BASE_PATH}/test-send`,
  send: `${ANNOUNCEMENTS_BASE_PATH}/send`,
} as const;
